import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { Panel, PanelHeader, PanelBody } from "../../components/panel/panel";
import { getWorkinstruction, saveWorkinstruction } from "../../services/workinstructions";
import { getUsers } from "../../services/users";
import ActionCard from "../../components/kanban/ActionCard";
import auth from "../../services/authservice";
import { connect } from 'react-redux';
import { getCurrentUser, loadCurrentUser } from '../../store/users';
import "./WorkInstructionBoard.css";
import CustomQRCode from "../../components/common/CustomQRCode";
import logo from "../../assets/images/logo.png";

const WorkInstructionBoard = ({ currentUser, loadCurrentUser }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [workInstruction, setWorkInstruction] = useState(null);
  const [actions, setActions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper method to get businessNo from user's accountNo if Company role
  const getBusinessNoFromUser = () => {
    const isCompanyRole = (currentUser?.roleName === "Company") || 
                         (currentUser?.role === "Company") ||
                         (currentUser?.role?.name === "Company");
    
    if (currentUser && isCompanyRole && currentUser?.accountNo) {
      return currentUser.accountNo;
    }
    return '';
  };

  // Helper method to create updated work instruction object (eliminates code duplication)
  const createUpdatedWorkInstruction = (actionsArray) => {
    const updatedWorkInstruction = {
      _id: workInstruction._id,
      user: typeof workInstruction.user === 'string' ? workInstruction.user : (workInstruction.user?._id || auth.getProfile()._id),
      department: workInstruction.department || '',
      name: workInstruction.name || '',
      description: workInstruction.description || '',
      tags: workInstruction.tags || '',
      version: workInstruction.version || '',
      language: workInstruction.language || '',
      actions: actionsArray,
      note: workInstruction.note || '',
      status: workInstruction.status || 'active',
      createdOn: workInstruction.createdOn || new Date(),
      editedOn: new Date()
    };

    // Set businessNo
    if (workInstruction.businessNo) {
      updatedWorkInstruction.businessNo = typeof workInstruction.businessNo === 'object' 
        ? workInstruction.businessNo._id 
        : workInstruction.businessNo;
    } else {
      const businessNo = getBusinessNoFromUser();
      if (businessNo) {
        updatedWorkInstruction.businessNo = businessNo;
      }
    }

    return updatedWorkInstruction;
  };

  useEffect(() => {
    const userProfile = auth.getProfile();
    if (userProfile?._id) {
      loadCurrentUser(userProfile._id);
    }
    
    fetchData();
  }, [id, loadCurrentUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [wiResponse, usersResponse] = await Promise.all([
        getWorkinstruction(id),
        getUsers()
      ]);

      setWorkInstruction(wiResponse.data);
      setUsers(usersResponse.data);
      
      // Use embedded actions from work instruction
      let workInstructionActions = [];
      if (wiResponse.data.actions && Array.isArray(wiResponse.data.actions)) {
        workInstructionActions = wiResponse.data.actions.map((embeddedAction, index) => ({
          _id: `action_${index}`, // Create unique ID for embedded actions
          name: embeddedAction.name || '',
          step: embeddedAction.step || (index + 1).toString()
        }));
      }
      
      const sortedActions = workInstructionActions.sort((a, b) => {
        const stepA = parseInt(a.step) || 0;
        const stepB = parseInt(b.step) || 0;
        return stepA - stepB;
      });

      setActions(sortedActions);
    } catch (err) {
      setError("Failed to load work instruction board");
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination } = result;
    
    if (source.index === destination.index) return;

    const newActions = Array.from(actions);
    const [reorderedAction] = newActions.splice(source.index, 1);
    newActions.splice(destination.index, 0, reorderedAction);

    // Update step numbers
    const updatedActions = newActions.map((action, index) => ({
      ...action,
      step: (index + 1).toString()
    }));

    setActions(updatedActions);

    try {
      // Convert back to embedded format and save to work instruction
      const embeddedActions = updatedActions.map(action => ({
        name: action.name || '',
        step: action.step || ''
      }));
      
      const updatedWorkInstruction = createUpdatedWorkInstruction(embeddedActions);
      
      await saveWorkinstruction(updatedWorkInstruction);
      // Refresh data to ensure consistency
      await fetchData();
    } catch (error) {
      // Revert on error
      await fetchData();
    }
  };

  const createNewAction = async () => {
    const newEmbeddedAction = {
      name: "New Action",
      step: (actions.length + 1).toString()
    };

    try {
      const updatedActions = [...(workInstruction.actions || []), newEmbeddedAction];
      
      // Clean actions
      const cleanActions = updatedActions.map(action => ({
        name: action.name || '',
        step: action.step || ''
      }));
      
      const updatedWorkInstruction = createUpdatedWorkInstruction(cleanActions);
      
      await saveWorkinstruction(updatedWorkInstruction);
      await fetchData();
    } catch (error) {
      setError("Failed to create new action: " + (error.response?.data || error.message || error));
    }
  };
  const handleActionUpdate = async (updatedAction) => {
    try {
      // Find the index of the action being updated
      const actionIndex = actions.findIndex(action => action._id === updatedAction._id);
      if (actionIndex === -1) {
        throw new Error("Action not found");
      }

      // Update the embedded actions in work instruction
      const updatedEmbeddedActions = workInstruction.actions.map((embeddedAction, index) => {
        if (index === actionIndex) {
          return {
            name: updatedAction.name || '',
            step: updatedAction.step || (index + 1).toString()
          };
        }
        return {
          name: embeddedAction.name || '',
          step: embeddedAction.step || ''
        };
      });

      const updatedWorkInstruction = createUpdatedWorkInstruction(updatedEmbeddedActions);

      await saveWorkinstruction(updatedWorkInstruction);
      await fetchData();
    } catch (error) {
      setError("Failed to update action: " + (error.response?.data || error.message || error));
    }
  };
  const handleActionDelete = async (actionId) => {
    try {
      // Find the index of the action to delete
      const actionIndex = actions.findIndex(action => action._id === actionId);
      if (actionIndex === -1) {
        throw new Error("Action not found");
      }

      // Remove the action from embedded actions array
      const updatedEmbeddedActions = workInstruction.actions.filter((_, index) => index !== actionIndex);
      
      // Renumber the steps
      const renumberedActions = updatedEmbeddedActions.map((action, index) => ({
        name: action.name || '',
        step: (index + 1).toString()
      }));

      const updatedWorkInstruction = createUpdatedWorkInstruction(renumberedActions);

      await saveWorkinstruction(updatedWorkInstruction);
      await fetchData();
    } catch (error) {
      setError("Failed to delete action: " + (error.response?.data || error.message || error));
    }
  };

  const handleGoBack = () => {
    navigate("/workinstructions/workinstructions");
  };

  if (loading) {
    return (
      <div className="board-loading">
        <div className="loading-spinner"></div>
        <p>Loading work instruction board...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="board-error">
        <p>{error}</p>
        <button onClick={handleGoBack} className="back-button">
          Back to Work Instructions
        </button>
      </div>
    );
  }

  if (!workInstruction) {
    return (
      <div className="board-error">
        <p>Work instruction not found</p>
        <button onClick={handleGoBack} className="back-button">
          Back to Work Instructions
        </button>
      </div>
    );
  }

  return (
    <div className="work-instruction-board">
      <div
        className="board-header"
        style={{display:"flex", justifyContent:"space-between"}}
      >
        <div>
          <button onClick={handleGoBack} className="back-button">
            ← Back to Work Instructions
          </button>
          <h1 className="board-title">{workInstruction.name}</h1>
          {workInstruction.description && (
            <p className="board-description">{workInstruction.description}</p>
          )}
        </div>
        <CustomQRCode logo={logo} value={id} />
      </div>

      <div className="board-content">
        {/* Left Column - Work Instruction Details */}
        <div className="work-instruction-details">
          <Panel>
            <PanelHeader>
              <div className="panel-header-content">
                <span>Work Instruction Details</span>
              </div>
            </PanelHeader>
            <PanelBody>
              <div className="details-container">
                <div className="detail-group">
                  <h3 className="detail-title">About</h3>
                  <div className="detail-item">
                    <strong>Name:</strong> 
                    <span>{workInstruction.name}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Description:</strong> 
                    <span>{workInstruction.description || 'No description'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Department:</strong> 
                    <span>{workInstruction.department || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Version:</strong> 
                    <span>{workInstruction.version || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Language:</strong> 
                    <span>{workInstruction.language || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Tags:</strong> 
                    <span>{workInstruction.tags || 'N/A'}</span>
                  </div>
                </div>

                <div className="detail-group">
                  <h3 className="detail-title">Owner Information</h3>
                  {workInstruction.user ? (
                    <div className="owner-section">
                      <div className="owner-info">
                        <div className="owner-avatars">
                          {/* User Avatar */}
                          {workInstruction.user.imageSrc ? (
                            <img 
                              src={workInstruction.user.imageSrc} 
                              alt="User Avatar"
                              className="owner-avatar-image"
                            />
                          ) : (
                            <div className="owner-avatar-placeholder">
                              <i className="fa fa-user"></i>
                            </div>
                          )}
                        </div>
                        <div className="owner-details">
                          <div className="owner-name">
                            {workInstruction.user.contactName?.first} {workInstruction.user.contactName?.last}
                          </div>
                          <div className="owner-email">
                            {workInstruction.user.email || 'No email'}
                          </div>
                          <div className="owner-username">
                            @{workInstruction.user.username || 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-owner">No owner assigned</div>
                  )}
                </div>

                <div className="detail-group">
                  <h3 className="detail-title">Business Information</h3>
                  {workInstruction.businessNo ? (
                    <div className="business-info">
                      <div className="business-section">
                        <div className="business-avatar">
                          {(() => {
                            const businessLogo = workInstruction?.companyinfo?.companies?.imageSrc || 
                                               workInstruction?.companyinfo?.imageSrc || 
                                               workInstruction?.businessNo?.companyInfo?.businessLogo || 
                                               workInstruction?.businessNo?.businessLogo || 
                                               workInstruction?.businessNo?.logo || 
                                               workInstruction?.businessNo?.companyInfo?.logo ||
                                               workInstruction?.businessNo?.image ||
                                               workInstruction?.businessNo?.companyInfo?.image;
                            
                            const finalLogo = businessLogo || (workInstruction?.user?.imageSrc);
                            
                            return finalLogo ? (
                              <img 
                                src={finalLogo} 
                                alt="Business Logo"
                                className="business-logo-image"
                                style={{ width: "20px", height: "20px", borderRadius: "50%", marginRight: "4px" }}
                              />
                            ) : (
                              <div className="business-logo-placeholder" style={{ 
                                width: "20px", 
                                height: "20px", 
                                borderRadius: "50%", 
                                backgroundColor: "#28a745",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                marginRight: "4px",
                                fontSize: "10px",
                                color: "white"
                              }}>
                                <i className="fa fa-building"></i>
                              </div>
                            );
                          })()}
                        </div>
                        <div className="business-details">
                          <div className="business-name">
                            {workInstruction?.businessNo?.companyInfo?.businessName || 
                             workInstruction?.businessNo?.name || 
                             'No Business'}
                          </div>
                          <div className="business-type">
                            Business Entity
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="no-business">No business assigned</div>
                  )}
                </div>

                <div className="detail-group">
                  <h3 className="detail-title">Additional Information</h3>
                  <div className="detail-item">
                    <strong>Created On:</strong> 
                    <span>{workInstruction.createdOn ? new Date(workInstruction.createdOn).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Edited On:</strong> 
                    <span>{workInstruction.editedOn ? new Date(workInstruction.editedOn).toLocaleDateString() : 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Notes:</strong> 
                    <span>{workInstruction.note || 'No notes'}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Total Actions:</strong> 
                    <span>{actions.length}</span>
                  </div>
                  <div className="detail-item">
                    <strong>Copyrights:</strong> 
                    <span>{workInstruction.copyright || ''}</span>
                  </div>                  
				  
                </div>
              </div>
            </PanelBody>
          </Panel>
<footer style={{ marginTop: '2rem', padding: '1rem 0', borderTop: '1px solid #ddd' }}>
  <p style={{ textAlign: 'center', fontSize: '0.9rem', color: '#666' }}>
    &copy; {new Date().getFullYear()} by Wappsolute. All rights reserved.
  </p>
</footer>
          
        </div>

        {/* Right Column - Action Steps */}
        <div className="action-steps">
          <Panel>
            <PanelHeader>
              <div className="panel-header-content">
                <span>Action Steps</span>
                <div>
                  <span className="action-count">{actions.length} actions</span>
                  <button 
                    onClick={createNewAction}
                    className="add-action-btn"
                    title="Add New Action"
                  >
                    + Add Action
                  </button>
                </div>
              </div>
            </PanelHeader>
            <PanelBody>
              <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="actions-list">
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`actions-container ${
                        snapshot.isDraggingOver ? "dragging-over" : ""
                      }`}
                    >
                      {actions.length === 0 ? (
                        <div className="empty-state">
                          <p>No actions found for this work instruction</p>
                          <p>Use the "Add Action" button above to create new actions</p>
                        </div>
                      ) : (
                        actions.map((action, index) => (
                          <ActionCard
                            key={action._id}
                            action={action}
                            index={index}
                            onUpdate={handleActionUpdate}
                            onDelete={handleActionDelete}
                            users={users}
                          />
                        ))
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </PanelBody>
          </Panel>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  currentUser: getCurrentUser(state)
});

const mapDispatchToProps = (dispatch) => ({
  loadCurrentUser: (id) => dispatch(loadCurrentUser(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(WorkInstructionBoard);
